import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useProfile } from "../../../../contexts/ProfileContext.tsx";
import LoadingComponent from "../../../../components/LoadingComponent/LoadingComponent.tsx";
import MotionSemAutorizacao from "../../../../core/components/MotionSemAutorizacao.tsx";
import NavbarV3 from "../../../../components/NavbarV3/NavbarV3.tsx";
import FiltroViagens from "../../Embarque/components/FiltroViagens/FiltroViagens.tsx";
import { useFiltro } from "../../../../contexts/FiltroContext.tsx";
import useService from "../../../../hooks/useService.ts";
import { emptyFiltroEmbarque } from "../../../../contexts/ContextConstants.ts";
import {
	FiltroEmbarqueCompleto,
	FiltroEmbarqueCompletoEnum,
} from "../../../../models/FiltroEmbarqueCompleto.ts";
import { buscarRelatorioEligibilidade } from "../../../../services/ModuloGerenciador/RelatorioEligibilidade.ts";
import {
	emptyGraficoPizza,
	GraficoPizza,
} from "../../../../models/GraficoPizza.ts";
import {
	emptyGraficoPizzaDouble,
	GraficoPizzaDouble,
} from "../../../../models/GraficoPizzaDouble.ts";
import Separator from "../../../../core/components/Separator.tsx";
import "./ElegibilidadeBrf.css";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { Tooltip } from "@mui/material";
import IconButton from "../../../../core/components/IconButton.tsx";
import { fix2DecimalString } from "../../../../core/utils/UtilityDates.ts";

function ElegibilidadeBrf() {
	const { profile, hasRegra } = useProfile();
	const {
		filtroEmbarque,
		clearFiltros,
		isFiltroModified,
		normalizarFiltroEmbarque,
	} = useFiltro();

	const [semAutorizacao, setSemAutorizacao] = useState(false);
	const [graficoQuantidade, setGraficoQuantidade] =
		useState<GraficoPizza>(emptyGraficoPizza);
	const [graficoFaturamento, setGraficoFaturamento] =
		useState<GraficoPizzaDouble>(emptyGraficoPizzaDouble);

	const filtroMemoized = useMemo(() => {
		return normalizarFiltroEmbarque(filtroEmbarque);
	}, [filtroEmbarque, normalizarFiltroEmbarque]);

	const [buscandoViagensFiltro, buscarViagensFiltro] = useService(
		(filtro: FiltroEmbarqueCompleto) =>
			buscarRelatorioEligibilidade({
				token: profile?.token!,
				filtro: filtro,
			}),
		{
			autoStart: false,
			onData: (response) => {
				setGraficoQuantidade(response.data.graficoQuantidade);
				setGraficoFaturamento(response.data.graficoFaturamento);
			},
		}
	);

	const [buscandoRelatorioInicial, buscarRelatorioInicial] = useService(
		() =>
			buscarRelatorioEligibilidade({
				token: profile?.token!,
				filtro: {
					...emptyFiltroEmbarque,
					[FiltroEmbarqueCompletoEnum.Contexto]: "todos",
				},
			}),
		{
			autoStart: false,
			onData: (response) => {
				setGraficoQuantidade(response.data.graficoQuantidade);
				setGraficoFaturamento(response.data.graficoFaturamento);
			},
		}
	);

	const handleBuscarViagens = useCallback(() => {
		buscarViagensFiltro(filtroMemoized);
	}, [buscarViagensFiltro, filtroMemoized]);

	const buscarViagensFiltroClipboard = useCallback(
		async (filtros: FiltroEmbarqueCompleto) => {
			buscarViagensFiltro(filtros);
		},
		[]
	);

	const limparMultiplosFiltros = useCallback(() => {
		clearFiltros();
		buscarRelatorioInicial();
	}, [profile?.token]);

	useEffect(() => {
		if (profile?.token) {
			setSemAutorizacao(!hasRegra("ElegebilidadeBRF"));
			if (isFiltroModified()) {
				handleBuscarViagens();
			} else {
				buscarRelatorioInicial();
			}
		}
	}, [profile?.token, hasRegra]);

	const dadosgraficoQuantidade = useMemo(() => {
		const options: ApexOptions = {
			chart: {
				height: 250,
				width: 720,
				type: "pie",
				zoom: {
					enabled: false,
				},
			},
			title: { text: graficoQuantidade.titulo },
			noData: { text: "Não há dados para serem exibidos" },
			labels: graficoQuantidade.titulos,
			colors:
				graficoQuantidade.titulo === "Embarques no Prazo" ||
				graficoQuantidade.titulo === "Entregas no Prazo"
					? ["#00e396", "#ff4560"]
					: undefined,
		};

		return {
			series: graficoQuantidade.valores.map((valor) =>
				parseFloat(valor.toFixed(2))
			),
			options,
			total: fix2DecimalString(graficoQuantidade.total),
		};
	}, [graficoQuantidade]);

	const dadosgraficoFaturamento = useMemo(() => {
		const options: ApexOptions = {
			chart: {
				height: 250,
				width: 720,
				type: "pie",
				zoom: {
					enabled: false,
				},
			},
			title: { text: graficoFaturamento.titulo },
			noData: { text: "Não há dados para serem exibidos" },
			labels: graficoFaturamento.titulos,
			colors:
				graficoFaturamento.titulo === "Embarques no Prazo" ||
				graficoFaturamento.titulo === "Entregas no Prazo"
					? ["#00e396", "#ff4560"]
					: undefined,
		};

		return {
			series: graficoFaturamento.valores.map((valor) =>
				parseFloat(valor.toFixed(2))
			),
			options,
			total: fix2DecimalString(graficoFaturamento.total),
		};
	}, [graficoFaturamento]);

	const isLoading = useMemo(() => {
		return !profile?.token || buscandoRelatorioInicial || buscandoViagensFiltro;
	}, [!profile?.token, buscandoRelatorioInicial, buscandoViagensFiltro]);

	if (semAutorizacao) {
		return <MotionSemAutorizacao />;
	}

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.5 }}
		>
			<NavbarV3 title={"Elegebilidade BRF"} />
			<AnimatePresence mode="wait">
				{isLoading && <LoadingComponent key="ElegebilidadeBRFLoading" />}
				{!isLoading && (
					<div
						key="ElegebilidadeBRFContent"
						className="elegebilidade-brf-content"
					>
						<FiltroViagens
							buscarViagens={handleBuscarViagens}
							buscarViagensClipboard={buscarViagensFiltroClipboard}
							onClearFiltros={limparMultiplosFiltros}
							showConfig={false}
							showDiariaExcedida={false}
						/>
						<div className="elegebilidade-brf-charts-container">
							<div className="elegebilidade-brf-charts-card">
								<Tooltip
									title={
										<>
											<b>Total: </b>
											<p>{dadosgraficoQuantidade.total}</p>
										</>
									}
								>
									<IconButton
										icon="Info"
										size="xl"
										theme="dark"
										backgroundColor="#FFFFFF"
										hoverColor="#CCC"
									/>
								</Tooltip>
								<ReactApexChart
									type="pie"
									series={dadosgraficoQuantidade.series}
									options={dadosgraficoQuantidade.options}
									width="500px"
									height={250}
								/>
							</div>
							<Separator direction="horizontal" size="lg" />
							<div className="elegebilidade-brf-charts-card">
								<Tooltip
									title={
										<>
											<b>Total: </b>
											<p>{dadosgraficoFaturamento.total}</p>
										</>
									}
								>
									<IconButton
										icon="Info"
										size="xl"
										theme="dark"
										backgroundColor="#FFFFFF"
										hoverColor="#CCC"
									/>
								</Tooltip>
								<ReactApexChart
									type="pie"
									series={dadosgraficoFaturamento.series}
									options={dadosgraficoFaturamento.options}
									width="500px"
									height={250}
								/>
							</div>
						</div>
					</div>
				)}
			</AnimatePresence>
		</motion.div>
	);
}

export default memo(ElegibilidadeBrf);
